import type {
  ConveyorObject,
  FactoryScene,
  ObjectKind,
  SceneObject,
  TransportObject,
} from "@/features/simulation/scene-types";
import { MAX_OBJECTS_PER_KIND } from "@/features/simulation/scene-types";
import type { TilePos } from "@/lib/pixi/isoCoords";

/**
 * Scene 操作・検証・(de)serialize ユーティリティ群。
 *
 * 純 TS 層 (React/Pixi/Zustand 非依存)。
 * すべての関数は immutable: 入力 Scene を変更せず新しい Scene を返す。
 */

// ─── 既定 Scene ──────────────────────────────────────────

/** 既定構成パラメータ (旧 DEFAULT_LAYOUT 相当) */
export const DEFAULT_SCENE_PARAMS = {
  conveyorLength: 12,
  pressColumn: 6,
  bufferCapacity: 3,
  transportCapacity: 3,
} as const;

/** 既定の固定タイル位置 (旧 constants の固定点) */
const DEFAULT_LOADING_TILE: TilePos = { col: 0, row: 10 };
const DEFAULT_UNLOADING_TILE: TilePos = { col: 2, row: 17 };
const DEFAULT_TRANSPORT_CORNER: TilePos = { col: 2, row: 12 };

/** conveyorLength と pressCol からデフォルト Scene を組み立てる */
export function createDefaultScene(
  overrides: {
    conveyorLength?: number;
    pressColumn?: number;
    bufferCapacity?: number;
    transportCapacity?: number;
  } = {},
): FactoryScene {
  const conveyorLength = overrides.conveyorLength ?? DEFAULT_SCENE_PARAMS.conveyorLength;
  const pressColumn = overrides.pressColumn ?? DEFAULT_SCENE_PARAMS.pressColumn;
  const bufferCapacity = overrides.bufferCapacity ?? DEFAULT_SCENE_PARAMS.bufferCapacity;
  const transportCapacity = overrides.transportCapacity ?? DEFAULT_SCENE_PARAMS.transportCapacity;

  const path: TilePos[] = [];
  for (let i = 0; i < conveyorLength; i++) {
    path.push({ col: DEFAULT_LOADING_TILE.col + i, row: DEFAULT_LOADING_TILE.row });
  }

  const conveyor: ConveyorObject = {
    id: "conveyor-1",
    kind: "conveyor",
    path,
    isEntry: true,
    bufferCapacity,
  };

  // press は path 上 (col=pressColumn, row=10) に配置
  const pressColClamped = clampPressColumn(pressColumn, conveyorLength);
  const press = {
    id: "press-1",
    kind: "press" as const,
    position: { col: pressColClamped, row: DEFAULT_LOADING_TILE.row },
    conveyorId: "conveyor-1",
  };

  // arm は conveyor 末端の 1 つ右
  const armCol = conveyorLength;
  const arm = {
    id: "arm-1",
    kind: "arm" as const,
    position: { col: armCol, row: DEFAULT_LOADING_TILE.row },
    // 西隣 (バッファ末端タイル) からピック、南隣 (transport parking) にプレース
    pickFromTile: { col: armCol - 1, row: DEFAULT_LOADING_TILE.row },
    placeToTile: { col: armCol, row: DEFAULT_LOADING_TILE.row + 1 },
  };

  // transport は parking → south corner → unloading
  const parking: TilePos = { col: armCol, row: DEFAULT_LOADING_TILE.row + 1 };
  const transport: TransportObject = {
    id: "transport-1",
    kind: "transport",
    parking,
    unloading: DEFAULT_UNLOADING_TILE,
    route: [
      parking,
      { col: parking.col, row: DEFAULT_TRANSPORT_CORNER.row },
      DEFAULT_TRANSPORT_CORNER,
      DEFAULT_UNLOADING_TILE,
    ],
    routeAutoMode: true,
    capacity: transportCapacity,
  };

  return {
    conveyors: [conveyor],
    presses: [press],
    arms: [arm],
    transports: [transport],
    sensors: [],
  };
}

// ─── path / 位置のヘルパー ──────────────────────────────────

/** path 内のタイル間が 4-近傍隣接で繋がっているかチェック */
export function isPathContinuous(path: readonly TilePos[]): boolean {
  for (let i = 0; i < path.length - 1; i++) {
    const a = path[i];
    const b = path[i + 1];
    if (!a || !b) return false;
    const dx = Math.abs(a.col - b.col);
    const dy = Math.abs(a.row - b.row);
    if (dx + dy !== 1) return false;
  }
  return true;
}

/** path の総距離 (タイル単位)。 path.length - 1 (セグメント数) */
export function pathTotalDistance(path: readonly TilePos[]): number {
  return Math.max(0, path.length - 1);
}

/**
 * path 上の連続位置 pathDist (0..length-1) からタイル座標 (実数) を補間。
 * 直線セグメント前提なので col/row 線形補間でよい。
 */
export function tileAtPathDistance(path: readonly TilePos[], pathDist: number): TilePos {
  if (path.length === 0) return { col: 0, row: 0 };
  if (path.length === 1) return path[0] as TilePos;
  const total = pathTotalDistance(path);
  const clamped = Math.max(0, Math.min(total, pathDist));
  const i = Math.floor(clamped);
  const frac = clamped - i;
  const a = path[i];
  const b = path[Math.min(i + 1, path.length - 1)];
  if (!a || !b) return { col: 0, row: 0 };
  return { col: a.col + (b.col - a.col) * frac, row: a.row + (b.row - a.row) * frac };
}

/** path 内で指定タイルがあれば pathDist (整数) を返す。なければ null */
export function pathDistanceOfTile(path: readonly TilePos[], tile: TilePos): number | null {
  for (let i = 0; i < path.length; i++) {
    const t = path[i];
    if (t && t.col === tile.col && t.row === tile.row) return i;
  }
  return null;
}

/** タイル等価判定 */
export function tileEquals(a: TilePos, b: TilePos): boolean {
  return a.col === b.col && a.row === b.row;
}

/** 4-近傍隣接判定 */
export function isAdjacent(a: TilePos, b: TilePos): boolean {
  return Math.abs(a.col - b.col) + Math.abs(a.row - b.row) === 1;
}

// ─── press position の有効範囲 (旧 pressPositionRange 相当) ──

export interface PressColumnRange {
  min: number;
  max: number;
}

/** 指定コンベア長に対する press カラムの有効範囲 (1..length-2) */
export function pressColumnRange(conveyorLength: number): PressColumnRange {
  return { min: 1, max: Math.max(1, conveyorLength - 2) };
}

/** press カラムを有効範囲にクランプして整数化 */
export function clampPressColumn(pressColumn: number, conveyorLength: number): number {
  const { min, max } = pressColumnRange(conveyorLength);
  const int = Math.round(pressColumn);
  return Math.min(max, Math.max(min, int));
}

// ─── 第 1 オブジェクトアクセサ (P1 単一インスタンス用) ──────

export function firstConveyor(scene: FactoryScene): ConveyorObject | undefined {
  return scene.conveyors[0];
}
export function firstPress(scene: FactoryScene): SceneObject | undefined {
  return scene.presses[0];
}
export function firstArm(scene: FactoryScene): SceneObject | undefined {
  return scene.arms[0];
}
export function firstTransport(scene: FactoryScene): TransportObject | undefined {
  return scene.transports[0];
}

// ─── 検証 ────────────────────────────────────────────────

export interface SceneValidationIssue {
  level: "error" | "warning";
  message: string;
  /** 関連オブジェクト id (任意) */
  objectId?: string;
}

/** 個数上限・整合性のチェック */
export function validateScene(scene: FactoryScene): SceneValidationIssue[] {
  const issues: SceneValidationIssue[] = [];

  // 個数上限
  const counts: Record<ObjectKind, number> = {
    conveyor: scene.conveyors.length,
    press: scene.presses.length,
    arm: scene.arms.length,
    transport: scene.transports.length,
    sensor: scene.sensors.length,
  };
  (Object.keys(counts) as ObjectKind[]).forEach((kind) => {
    if (counts[kind] > MAX_OBJECTS_PER_KIND[kind]) {
      issues.push({
        level: "error",
        message: `${kind} の数 (${counts[kind]}) が上限 ${MAX_OBJECTS_PER_KIND[kind]} を超過`,
      });
    }
  });

  // path 連続性
  for (const c of scene.conveyors) {
    if (c.path.length < 2) {
      issues.push({ level: "error", message: `${c.id}: path が短すぎます (>=2 タイル必要)`, objectId: c.id });
    }
    if (!isPathContinuous(c.path)) {
      issues.push({ level: "error", message: `${c.id}: path が連続していません`, objectId: c.id });
    }
  }

  // press は所属コンベアの path 上にあるか
  for (const p of scene.presses) {
    const c = scene.conveyors.find((cv) => cv.id === p.conveyorId);
    if (!c) {
      issues.push({ level: "error", message: `${p.id}: 所属コンベア ${p.conveyorId} が存在しません`, objectId: p.id });
      continue;
    }
    if (pathDistanceOfTile(c.path, p.position) === null) {
      issues.push({
        level: "error",
        message: `${p.id}: コンベア ${p.conveyorId} の path 上にありません`,
        objectId: p.id,
      });
    }
  }

  // arm の pick/place が隣接タイルか
  for (const a of scene.arms) {
    if (!isAdjacent(a.position, a.pickFromTile)) {
      issues.push({
        level: "warning",
        message: `${a.id}: pickFromTile がアーム位置と隣接していません`,
        objectId: a.id,
      });
    }
    if (!isAdjacent(a.position, a.placeToTile)) {
      issues.push({
        level: "warning",
        message: `${a.id}: placeToTile がアーム位置と隣接していません`,
        objectId: a.id,
      });
    }
  }

  // transport の route 端点が parking / unloading と一致
  for (const t of scene.transports) {
    if (t.route.length < 2) {
      issues.push({ level: "error", message: `${t.id}: route のウェイポイント不足`, objectId: t.id });
      continue;
    }
    const first = t.route[0];
    const last = t.route[t.route.length - 1];
    if (first && !tileEquals(first, t.parking)) {
      issues.push({ level: "warning", message: `${t.id}: route 先頭が parking と一致しません`, objectId: t.id });
    }
    if (last && !tileEquals(last, t.unloading)) {
      issues.push({ level: "warning", message: `${t.id}: route 末尾が unloading と一致しません`, objectId: t.id });
    }
  }

  return issues;
}

/** Error レベルの issue があるかどうか */
export function sceneHasErrors(scene: FactoryScene): boolean {
  return validateScene(scene).some((i) => i.level === "error");
}

// ─── 直線コンベアの auto-route (parking → unloading) ────────

/**
 * 2 点を結ぶウェイポイント列を直交移動で生成 (L 字)。
 * (col, row) 同じならその点 1 つ、col/row どちらかが同じなら直線、
 * それ以外は L 字 (一旦 col 方向に合わせてから row 方向に進む)。
 */
export function autoRouteBetween(start: TilePos, end: TilePos): TilePos[] {
  if (tileEquals(start, end)) return [start];
  if (start.col === end.col || start.row === end.row) return [start, end];
  // L 字: 先に row 方向に進む選択肢
  const corner: TilePos = { col: start.col, row: end.row };
  return [start, corner, end];
}

// ─── id 採番 ───────────────────────────────────────────

/** 既存 id と衝突しない新 id を採番 */
export function nextObjectId(scene: FactoryScene, kind: ObjectKind): string {
  const allIds = new Set<string>([
    ...scene.conveyors.map((o) => o.id),
    ...scene.presses.map((o) => o.id),
    ...scene.arms.map((o) => o.id),
    ...scene.transports.map((o) => o.id),
    ...scene.sensors.map((o) => o.id),
  ]);
  let i = 1;
  while (allIds.has(`${kind}-${i}`)) i += 1;
  return `${kind}-${i}`;
}

// ─── (de)serialize ────────────────────────────────────────

/**
 * Scene から旧 Layout 互換ビューを派生させる。
 * 第 1 オブジェクトのプロパティを単純に拾うだけ。
 */
export function deriveLayoutFromScene(scene: FactoryScene): {
  conveyorLength: number;
  pressPosition: number;
  bufferCapacity: number;
  transportCapacity: number;
} {
  const c = firstConveyor(scene);
  const p = scene.presses[0];
  const t = firstTransport(scene);
  return {
    conveyorLength: c?.path.length ?? DEFAULT_SCENE_PARAMS.conveyorLength,
    pressPosition: p?.position.col ?? DEFAULT_SCENE_PARAMS.pressColumn,
    bufferCapacity: c?.bufferCapacity ?? DEFAULT_SCENE_PARAMS.bufferCapacity,
    transportCapacity: t?.capacity ?? DEFAULT_SCENE_PARAMS.transportCapacity,
  };
}

/** Scene の JSON 文字列化 (整形あり) */
export function serializeScene(scene: FactoryScene): string {
  return JSON.stringify(scene, null, 2);
}

/**
 * JSON 文字列から Scene を復元。
 * 必須フィールドの存在のみ最小チェック。詳細検証は validateScene で。
 */
export function deserializeScene(json: string): FactoryScene {
  const data = JSON.parse(json);
  if (
    typeof data !== "object" ||
    data === null ||
    !Array.isArray(data.conveyors) ||
    !Array.isArray(data.presses) ||
    !Array.isArray(data.arms) ||
    !Array.isArray(data.transports) ||
    !Array.isArray(data.sensors)
  ) {
    throw new Error("Invalid scene JSON: missing arrays");
  }
  return data as FactoryScene;
}
